import {
  FeatureFlagEntry,
  FeatureFlagGroup,
  FeatureFlagConfig,
  FeatureManagementSource
} from 'v5/services/front/FeatureManagementService/types';

export type FeatureFlagScopeKey = 'legacy' | 'frontend' | `workspace-${number}`;

export type FeatureFlagScopeSection = {
  name: string;
  scopes: Array<{
    key: FeatureFlagScopeKey;
    label: string;
    kind: 'legacy' | 'frontend' | 'workspace';
    workspaceId?: number;
  }>;
};

export function scopeKeyToSource(
  key: FeatureFlagScopeKey
): FeatureManagementSource {
  if (key === 'legacy' || key === 'frontend') {
    return key;
  }
  const workspaceId = Number(key.slice('workspace-'.length));
  return { workspaceId };
}

export function getConfigForScope(
  entry: FeatureFlagEntry,
  key: FeatureFlagScopeKey
): FeatureFlagConfig {
  if (key === 'legacy') {
    return entry.config.legacy;
  }
  if (key === 'frontend') {
    return entry.config.frontend;
  }
  const workspaceId = Number(key.slice('workspace-'.length));
  return entry.config.workspaces[workspaceId] ?? { supported: false };
}

export function entrySupportsScope(
  entry: FeatureFlagEntry,
  key: FeatureFlagScopeKey
): boolean {
  return getConfigForScope(entry, key).supported;
}

export function anyEntrySupportsScope(
  entries: readonly FeatureFlagEntry[],
  key: FeatureFlagScopeKey
): boolean {
  return entries.some(entry => entrySupportsScope(entry, key));
}

/**
 * Builds sidebar sections for by-workspace mode: Global (Legacy, This browser)
 * then Workspaces. Omits scopes that no flag supports.
 */
export function buildFeatureFlagScopeSections(
  groups: FeatureFlagGroup[],
  workspaceColumns: ReadonlyArray<{ id: number; name: string }>
): FeatureFlagScopeSection[] {
  const allEntries = groups.flatMap(group => group.entries);
  const sections: FeatureFlagScopeSection[] = [];

  const globalScopes: FeatureFlagScopeSection['scopes'] = [];
  if (anyEntrySupportsScope(allEntries, 'legacy')) {
    globalScopes.push({
      key: 'legacy',
      label: 'Legacy',
      kind: 'legacy'
    });
  }
  if (anyEntrySupportsScope(allEntries, 'frontend')) {
    globalScopes.push({
      key: 'frontend',
      label: 'This browser',
      kind: 'frontend'
    });
  }
  if (globalScopes.length > 0) {
    sections.push({ name: 'Global', scopes: globalScopes });
  }

  const workspaceScopes = workspaceColumns
    .filter(column =>
      anyEntrySupportsScope(allEntries, `workspace-${column.id}`)
    )
    .map(column => ({
      key: `workspace-${column.id}` as FeatureFlagScopeKey,
      label: column.name,
      kind: 'workspace' as const,
      workspaceId: column.id
    }));

  if (workspaceScopes.length > 0) {
    sections.push({ name: 'Workspaces', scopes: workspaceScopes });
  }

  return sections;
}

export function matchesFeatureFlagScopeSearch(
  scope: FeatureFlagScopeSection['scopes'][number],
  query: string
): boolean {
  if (!query) {
    return true;
  }
  return (
    scope.label.toLowerCase().includes(query) ||
    scope.key.toLowerCase().includes(query) ||
    scope.kind.toLowerCase().includes(query)
  );
}

/**
 * Filters by-workspace sidebar sections by search query (sidebar items only).
 * Empty sections are dropped.
 */
export function filterFeatureFlagScopeSections(
  sections: FeatureFlagScopeSection[],
  query: string
): FeatureFlagScopeSection[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return sections;
  }

  return sections
    .map(section => ({
      ...section,
      scopes: section.scopes.filter(scope =>
        matchesFeatureFlagScopeSearch(scope, normalized)
      )
    }))
    .filter(section => section.scopes.length > 0);
}

export function isScopeDirty(
  key: FeatureFlagScopeKey,
  dirty: {
    changedLegacyFlagIds: readonly string[];
    changedFrontendFlagIds: readonly string[];
    changedWorkspaceFlagIds: Readonly<Record<number, readonly string[]>>;
  }
): boolean {
  if (key === 'legacy') {
    return dirty.changedLegacyFlagIds.length > 0;
  }
  if (key === 'frontend') {
    return dirty.changedFrontendFlagIds.length > 0;
  }
  const workspaceId = Number(key.slice('workspace-'.length));
  return (dirty.changedWorkspaceFlagIds[workspaceId] ?? []).length > 0;
}

export function dirtyFeatureIdsForScope(
  key: FeatureFlagScopeKey,
  dirty: {
    changedLegacyFlagIds: readonly string[];
    changedFrontendFlagIds: readonly string[];
    changedWorkspaceFlagIds: Readonly<Record<number, readonly string[]>>;
  }
): ReadonlySet<string> {
  if (key === 'legacy') {
    return new Set(dirty.changedLegacyFlagIds);
  }
  if (key === 'frontend') {
    return new Set(dirty.changedFrontendFlagIds);
  }
  const workspaceId = Number(key.slice('workspace-'.length));
  return new Set(dirty.changedWorkspaceFlagIds[workspaceId] ?? []);
}

export function pickPreferredScopeKey(
  sections: FeatureFlagScopeSection[],
  dirty: {
    changedLegacyFlagIds: readonly string[];
    changedFrontendFlagIds: readonly string[];
    changedWorkspaceFlagIds: Readonly<Record<number, readonly string[]>>;
  },
  current: FeatureFlagScopeKey | null
): FeatureFlagScopeKey | null {
  const allKeys = sections.flatMap(section =>
    section.scopes.map(scope => scope.key)
  );
  if (current && allKeys.includes(current)) {
    return current;
  }
  const dirtyKey = allKeys.find(key => isScopeDirty(key, dirty));
  return dirtyKey ?? allKeys[0] ?? null;
}
